import { Component, ViewChild, ElementRef } from '@angular/core'
import { CurrencyPipe } from '@angular/common'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, Grid } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

interface PoHeader {
    poNumber: string | null
    poDate: string | null
    vendorName: string | null
    vendorAddress: string | null
    vendorPhone: string | null
    vendorEmail: string | null
    buyerCompany: string | null
    buyerName: string | null
    shipToAddress: string | null
    billToAddress: string | null
    paymentTerms: string | null
    deliveryDate: string | null
    currency: string | null
    projectNumber: string | null
    notes: string | null
}

interface PoLineItem {
    lineNumber: number | null
    itemCode: string | null
    description: string | null
    unitOfMeasure: string | null
    quantity: number | null
    unitPrice: number | null
    lineTotal: number | null
    glAccountCode: string | null
}

interface PoTotals {
    subtotal: number | null
    taxAmount: number | null
    freightAmount: number | null
    grandTotal: number | null
}

interface PoExtractionResult {
    header: PoHeader
    lineItems: PoLineItem[]
    totals: PoTotals
}

@Component({
    selector: 'app-ai-poc',
    templateUrl: './ai-poc.component.html',
    standalone: true,
    imports: [CurrencyPipe, WjGridModule, WjGridFilterModule, ActionToolbarCount, AutoSizeDirective],
})
export class AiPocComponent extends BaseComponent {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>

    public grid: Grid<PoLineItem>
    public isProcessing = false
    public errorMessage: string | null = null
    public result: PoExtractionResult | null = null
    public uploadedFileName: string | null = null

    constructor() {
        super()
        this.grid = new Grid<PoLineItem>(this.services).filterBy(item => [item.description, item.itemCode])
        this.isInitialLoading = false
    }

    public triggerFileInput() {
        this.fileInput.nativeElement.click()
    }

    public onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files || input.files.length === 0) return

        const file = input.files[0]
        this.uploadedFileName = file.name
        this.uploadFile(file)
        input.value = ''
    }

    private uploadFile(file: File) {
        this.isProcessing = true
        this.errorMessage = null
        this.result = null

        const formData = new FormData()
        formData.append('file', file, file.name)

        this.cancelableRequest(
            'extract',
            true,
            this.services.data.http.post<PoExtractionResult>('/api/AiPoc/ExtractPo', formData).subscribe({
                next: result => {
                    this.isProcessing = false
                    this.result = result
                    this.grid.items = result.lineItems || []
                    this.services.notification.success('PO data extracted successfully!')
                },
                error: error => {
                    this.isProcessing = false
                    const errorMsg = error.error?.error || error.message || 'An error occurred while extracting PO data'
                    this.errorMessage = errorMsg
                    this.services.notification.error(errorMsg)
                },
            }),
        )
    }

    public get headerFields(): { label: string; value: string | null }[] {
        if (!this.result?.header) return []
        const h = this.result.header
        return [
            { label: 'PO Number', value: h.poNumber },
            { label: 'PO Date', value: h.poDate },
            { label: 'Vendor Name', value: h.vendorName },
            { label: 'Vendor Address', value: h.vendorAddress },
            { label: 'Vendor Phone', value: h.vendorPhone },
            { label: 'Vendor Email', value: h.vendorEmail },
            { label: 'Buyer Company', value: h.buyerCompany },
            { label: 'Buyer Name', value: h.buyerName },
            { label: 'Ship-To Address', value: h.shipToAddress },
            { label: 'Bill-To Address', value: h.billToAddress },
            { label: 'Payment Terms', value: h.paymentTerms },
            { label: 'Delivery Date', value: h.deliveryDate },
            { label: 'Currency', value: h.currency },
            { label: 'Project Number', value: h.projectNumber },
            { label: 'Notes', value: h.notes },
        ].filter(f => f.value != null)
    }
}
